import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { FilterValue, FilterValueType } from '../types';

/**
 * Filter Operator for comparing field value is greater than the filter value
 */
export function greaterThan<T>(
	row: T,
	fields: (keyof T)[],
	filterValueType: FilterValueType,
	filterValue: FilterValue,
	fieldProperty?: string
) {
	return executeFilter(false, row, fields, filterValueType, filterValue, fieldProperty);
}

/**
 * Filter Operator for comparing field value is greater than or equal to the filter value
 */
export function greaterThanOrEqualTo<T>(
	row: T,
	fields: (keyof T)[],
	filterValueType: FilterValueType,
	filterValue: FilterValue,
	fieldProperty?: string
) {
	return executeFilter(true, row, fields, filterValueType, filterValue, fieldProperty);
}

/**
 * Local method for executing the filter since they are the same logic besides including an = or not
 */
function executeFilter<T>(
	includeEquals: boolean,
	row: T,
	fields: (keyof T)[],
	filterValueType: FilterValueType,
	filterValue: FilterValue,
	fieldProperty?: string
) {
	const method = `Greater Than${includeEquals ? ' or Equal To' : ''}`;

	// We are expecting a string or number to compare, if it is not that than we cannot compare
	if (typeof filterValue !== 'string' && typeof filterValue !== 'number') {
		throw new Error(`${method} operator received invalid comparison value of ${typeof filterValue}`);
	}

	const isGreaterThan: boolean = fields.reduce((fieldIsGreaterThan, field) => {
		// Since we are always using OR logic, once we find a single match the rest doesn't matter just continue
		if (fieldIsGreaterThan) {
			return true;
		}

		let fieldValue: unknown = row[field];
		if (fieldValue && typeof fieldValue === 'object' && fieldProperty) {
			const obj = fieldValue as Record<string, unknown>;
			fieldValue = obj[fieldProperty];
		}

		// For Greater Than Operator we need an actual value to compare
		if (fieldValue === null || fieldValue === undefined) {
			return false;
		}

		// Do specific date logic if applicable. We should already have it stored as unix from the API transform.
		if (filterValueType === 'date') {
			if (typeof fieldValue === 'number' && typeof filterValue === 'number') {
				// The filter Value for a date should contain the amount of days to add/subtract to today. So today = 0, tomorrow = 1, yesterday = -1, etc.
				const now = dayjsNow().add(filterValue, 'd');
				const bod = now.startOf('d').valueOf();
				const eod = now.endOf('d').valueOf();

				// Use Start of Day if >=, use End of Day if >
				return includeEquals ? fieldValue >= bod : fieldValue > eod;
			} else {
				throw new Error(`${method} operator errored trying to compare unix dates of types ${typeof fieldValue} and ${typeof filterValue}`);
			}
		}

		switch (typeof fieldValue) {
			case 'number':
				return includeEquals ? fieldValue >= Number(filterValue) : fieldValue > Number(filterValue);

			default:
				throw new Error(`${method} operator cannot be applied to ${typeof fieldValue}`);
		}
	}, false);

	return isGreaterThan;
}
